import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import store from '@/store'

// Pages (lazy-loaded)
const Home      = () => import('@/views/Home.vue')
const Login     = () => import('@/views/Login.vue')
const MyProfile = () => import('@/views/MyProfile.vue')
// Admin Pages
const AdminDashboard     = () => import('@/views/AdminDashboard.vue')
const AdminUsers         = () => import('@/views/AdminUsers.vue')
const AdminUserDetails   = () => import('@/views/AdminUserDetails.vue')
const AdminAssessments   = () => import('@/views/AdminAssessments.vue')
const AdminBanks         = () => import('@/views/AdminBanks.vue')
const AdminBankDetails   = () => import('@/views/AdminBankDetails.vue')
const AdminBankUsers     = () => import('@/views/AdminBankUsers.vue')
const AdminBankCustomers = () => import('@/views/AdminBankCustomers.vue')
const AdminBankCustomerDetails = () => import('@/views/AdminBankCustomerDetails.vue')
const AdminEditAssessment = () => import('@/views/AdminAssessment.vue')
// Bank Pages
const BankDashboard = () => import('@/views/BankDashboard.vue')
const BankUsers     = () => import('@/views/BankUsers.vue')
const BankCustomers = () => import('@/views/BankCustomers.vue')
const BankCustomerDetails = () => import('@/views/BankCustomerDetails.vue')
const BankCustomerAssessmentReview = () => import('@/views/BankCustomerAssessmentReview.vue')
// Customer Pages
const CustomerDashboard    = () => import('@/views/CustomerDashboard.vue')
const RiskAssessment       = () => import('@/views/RiskAssessment.vue')
const RiskAssessmentReview = () => import('@/views/RiskAssessmentReview.vue')
// Not Found
const NotFound = () => import('@/views/NotFound.vue')

Vue.use(VueRouter)

const permitAdminOnly = async function (to, from, next) {
  if (!store.getters.initializationComplete) {
    await store.dispatch('initialize')
  }
  // Not logged in
  if (!store.getters.sessionUserIsFullyLoggedIn) {
    if (from.name !== 'Login') next('/login')
    return
  }
  // Password Reset Required
  if (store.getters.isPwResetRequired) {
    if (from.name !== 'My Profile') next('/my-profile')
    return
  }
  // Not Admin
  if (!store.getters.sessionUserIsAdmin) {
    // next(store.getters.sessionUserHomePage)
    next('/not-found')
    return
  }
  // OK
  next()
}

const permitBankOrAdmin = async function (to, from, next) {
  if (!store.getters.initializationComplete) {
    await store.dispatch('initialize')
  }
  // Not logged in
  if (!store.getters.sessionUserIsFullyLoggedIn) {
    if (from.name !== 'Login') next('/login')
    return
  }
  // Password Reset Required
  if (store.getters.isPwResetRequired) {
    if (from.name !== 'My Profile') next('/my-profile')
    return
  }
  // Not Bank or Admin
  if (!store.getters.sessionUserIsBankOrAdmin) {
    // next(store.getters.sessionUserHomePage)
    next('/not-found')
    return
  }
  // OK
  next()
}

const permitCustomer = async function (to, from, next) {
  if (!store.getters.initializationComplete) {
    await store.dispatch('initialize')
  }
  // Not logged in
  if (!store.getters.sessionUserIsFullyLoggedIn) {
    if (from.name !== 'Login') next('/login')
    return
  }
  // Password Reset Required
  if (store.getters.isPwResetRequired) {
    if (from.name !== 'My Profile') next('/my-profile')
    return
  }
  // OK
  next()
}

const permitUser = async function (to, from, next) {
  if (!store.getters.initializationComplete) {
    await store.dispatch('initialize')
  }
  // Not logged in
  if (!store.getters.sessionUserIsFullyLoggedIn) {
    next('/login')
    return
  }
  // OK
  next()
}

// Routing
const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/my-profile',
    name: 'My Profile',
    component: MyProfile,
    beforeEnter: permitUser
  },

  // Admin Pages
  {
    path: '/admin',
    name: 'Admin',
    component: AdminDashboard,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/assessments',
    name: 'Admin Assessments',
    component: AdminAssessments,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/users',
    name: 'Admin Users',
    component: AdminUsers,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/users/:userId',
    name: 'User Details',
    component: AdminUserDetails,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/banks',
    name: 'Banks',
    component: AdminBanks,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/banks/:bankId',
    name: 'Bank Details',
    component: AdminBankDetails,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/banks/:bankId/users',
    name: 'Bank Users (Admin)',
    component: AdminBankUsers,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/banks/:bankId/customers',
    name: 'Bank Customers (Admin)',
    component: AdminBankCustomers,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/customers/:custId',
    name: 'Admin Customer Details',
    component: AdminBankCustomerDetails,
    beforeEnter: permitAdminOnly
  },
  {
    path: '/admin/assessments/:assessmentId',
    name: 'Assessment',
    component: AdminEditAssessment,
    beforeEnter: permitAdminOnly
  },

  // Bank Pages
  {
    path: '/dashboard',
    name: 'Bank Dashboard',
    component: BankDashboard,
    beforeEnter: permitBankOrAdmin
  },
  {
    path: '/customers',
    name: 'Bank Customers',
    component: BankCustomers,
    beforeEnter: permitBankOrAdmin
  },
  {
    path: '/customers/:custId',
    name: 'Bank Customer Details',
    component: BankCustomerDetails,
    beforeEnter: permitBankOrAdmin
  },
  {
    path: '/customers/:custId/submissions/:subId',
    name: 'Bank Customer Submission Review',
    component: BankCustomerAssessmentReview,
    beforeEnter: permitBankOrAdmin
  },
  // {
  //   path: '/users',
  //   name: 'Bank Users',
  //   component: BankUsers,
  //   beforeEnter: permitBankOrAdmin
  // },

  // Customer Pages
  {
    path: '/risk-assessment',
    name: 'Customer Dashboard',
    component: CustomerDashboard,
    beforeEnter: permitCustomer
  },
  {
    path: '/risk-assessment/current',
    name: 'Risk Assessment',
    component: RiskAssessment,
    beforeEnter: permitCustomer
  },
  {
    path: '/risk-assessment/review/:subId',
    name: 'Risk Assessment Review',
    component: RiskAssessmentReview,
    beforeEnter: permitCustomer
  },
  // Catch All
  {
    path: '*',
    name: 'Not Found',
    component: NotFound
  }  
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

export default router
